import './screens.css';
import { Link } from 'react-router-dom';
import { useLearner } from '@/state/learnerStore';
import type { LearnerState } from '@/types/learner';
import { MODULES_SEED, type ModuleMeta, type LessonMeta } from '@/seed/modules';
import { LESSONS } from '@/seed/lessons';

/**
 * Learn — the curriculum index. Modules and their lessons come straight
 * from the seed (src/seed/modules.ts); a lesson is a live link only when
 * its full interior is authored in src/seed/lessons.ts (LESSONS), otherwise
 * it shows as "Coming soon". Module lock/current/complete state mirrors the
 * dashboard learning path, driven by the learner store.
 */
type ModuleState = 'complete' | 'current' | 'locked';

export default function LearnScreen() {
  const learner = useLearner();
  return (
    <div className="screen">
      <div>
        <p className="screen__eyebrow">Learn</p>
        <h1 className="screen__title">Tracks, lessons, and specialty modules</h1>
        <p className="screen__sub">
          Where the curriculum lives. Foundational modules build the advice-driven sales motion —
          discovery before quote, reading the household, earning the close. Each lesson is a short,
          repeatable burst the coach narrates while you react.
        </p>
      </div>

      <section className="module-list">
        {MODULES_SEED.map((m) => (
          <ModuleCard key={m.module} module={m} learner={learner} />
        ))}
      </section>
    </div>
  );
}

function ModuleCard({ module: m, learner }: { module: ModuleMeta; learner: LearnerState }) {
  const state = moduleState(m, learner);
  const stateLabel =
    state === 'complete' ? 'Complete' : state === 'current' ? 'In progress' : 'Locked';
  return (
    <article className={`module-card module-card--${state}`}>
      <header className="module-card__head">
        <span className="module-card__num">M{m.module}</span>
        <h2 className="module-card__title">{m.title}</h2>
        <span className={`module-card__state module-card__state--${state}`}>{stateLabel}</span>
      </header>
      {m.lessons.length === 0 ? (
        <p className="module-card__empty">Lessons coming soon.</p>
      ) : (
        <ol className="lesson-list">
          {m.lessons.map((lesson) => (
            <LessonRow
              key={lesson.lesson_id}
              lesson={lesson}
              module={m.module}
              moduleState={state}
              learner={learner}
            />
          ))}
        </ol>
      )}
    </article>
  );
}

function LessonRow({
  lesson,
  module,
  moduleState: mState,
  learner,
}: {
  lesson: LessonMeta;
  module: number;
  moduleState: ModuleState;
  learner: LearnerState;
}) {
  const authored = Boolean(LESSONS[lesson.lesson_id]);
  const isCurrent = learner.current_module === module && learner.current_lesson === lesson.lesson;
  // A lesson is enterable when its interior is authored and its module is
  // unlocked (current or already complete). Locked-module lessons preview only.
  const enterable = authored && mState !== 'locked';
  const cls = isCurrent
    ? 'lesson-row lesson-row--current'
    : enterable
      ? 'lesson-row lesson-row--available'
      : 'lesson-row lesson-row--soon';

  const inner = (
    <>
      <span className="lesson-row__num">L{lesson.lesson}</span>
      <span className="lesson-row__title">{lesson.title}</span>
      <span className="lesson-row__cta">
        {isCurrent ? 'Resume →' : enterable ? 'Start →' : authored ? 'Locked' : 'Coming soon'}
      </span>
    </>
  );

  return enterable ? (
    <li className={cls}>
      <Link to={`/lesson/${lesson.lesson_id}`} className="lesson-row__link">
        {inner}
      </Link>
    </li>
  ) : (
    <li className={cls}>
      <div className="lesson-row__link lesson-row__link--static">{inner}</div>
    </li>
  );
}

function moduleState(m: ModuleMeta, learner: LearnerState): ModuleState {
  if (learner.modules_complete >= m.module) return 'complete';
  if (learner.current_module === m.module) return 'current';
  return 'locked';
}
